import type {
  DefaultServerService,
  ServersService,
  WorkspaceServerService,
} from '@affine/core/modules/cloud';
import type { WorkspaceService } from '@affine/core/modules/workspace';
import { WorkspaceLocalCacheService } from '@affine/core/modules/workspace-local-cache';
import {
  type CalendarDisplaySettings,
  decodeCalendarDisplaySettings,
  DEFAULT_CALENDAR_DISPLAY_SETTINGS,
} from '@blocksuite/data-view';
import { Service } from '@toeverything/infra';
import { distinctUntilChanged, map, switchMap } from 'rxjs';

const NAMESPACE = 'planner';
const SEGMENT = 'display-v1';
const SELECTION_SEGMENT = 'view-selection-v1';
const LOCAL_ANONYMOUS = 'local-anonymous';

export interface PlannerSourceRef {
  blockId: string;
  databaseId: string;
  docId: string;
  viewId: string;
}

export type PlannerSourceSelectionKey = string;

export interface PlannerViewSelectionV1 {
  version: 1;
  views: Record<PlannerSourceSelectionKey, { gantt?: string; kanban?: string }>;
}

export function plannerSourceSelectionKey(
  ref: PlannerSourceRef
): PlannerSourceSelectionKey {
  const values = [ref.docId, ref.blockId, ref.databaseId, ref.viewId];
  if (values.some(value => typeof value !== 'string' || !value.trim())) {
    throw new Error('Planner source selection IDs must be nonblank');
  }
  return JSON.stringify(values);
}

export function decodePlannerViewSelectionV1(
  value: unknown
): PlannerViewSelectionV1 | null {
  if (!exactObject(value, ['version', 'views']) || value.version !== 1)
    return null;
  if (
    !value.views ||
    typeof value.views !== 'object' ||
    Array.isArray(value.views)
  )
    return null;
  for (const [key, selection] of Object.entries(value.views)) {
    if (!isCanonicalSelectionKey(key)) return null;
    if (!exactObject(selection, Object.keys(selection))) return null;
    const keys = Object.keys(selection);
    if (keys.some(item => item !== 'gantt' && item !== 'kanban')) return null;
    if (
      Object.values(selection).some(
        item => typeof item !== 'string' || !item.trim()
      )
    )
      return null;
  }
  return value as unknown as PlannerViewSelectionV1;
}

export type PlannerDisplaySettingsLoadResult =
  | { status: 'corrupt' | 'scope_changed' | 'unavailable' }
  | { settings: CalendarDisplaySettings; status: 'ready' };

export type PlannerViewSelectionLoadResult =
  | { status: 'corrupt' | 'scope_changed' | 'unavailable' }
  | { selection: PlannerViewSelectionV1; status: 'ready' };

/** Presentation-only request/response service. No bus: work is small, local and non-retryable. */
export class PlannerPresentationSettingsService extends Service {
  private readonly local = new Map<string, CalendarDisplaySettings>();
  private readonly localSelections = new Map<string, PlannerViewSelectionV1>();
  private generation = 0;
  private localRealm: string | null = null;
  private selectionUpdateQueue: Promise<void> = Promise.resolve();

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly defaultServerService: DefaultServerService,
    private readonly serversService: ServersService,
    private readonly cache: WorkspaceLocalCacheService
  ) {
    super();
    const invalidate = () => {
      this.generation += 1;
      this.local.clear();
      this.localSelections.clear();
      this.localRealm = null;
    };
    const accountSubscription = this.workspaceServerService.server$
      .pipe(
        switchMap(
          server => (server ?? this.defaultServerService.server).account$
        ),
        distinctUntilChanged((previous, next) => previous?.id === next?.id)
      )
      .subscribe(invalidate);
    const scopeSubscription = this.cache.scope$.subscribe(invalidate);
    const serverSetSubscription = this.serversService.servers$
      .pipe(
        map(servers =>
          servers
            .map(server => server.id)
            .sort()
            .join('\u0000')
        ),
        distinctUntilChanged()
      )
      .subscribe(invalidate);
    this.disposables.push(() => scopeSubscription.unsubscribe());
    this.disposables.push(() => accountSubscription.unsubscribe());
    this.disposables.push(() => serverSetSubscription.unsubscribe());
  }

  async load(): Promise<PlannerDisplaySettingsLoadResult> {
    if (this.workspaceService.workspace.flavour === 'local') {
      this.syncLocalRealm();
      return {
        settings:
          this.local.get(this.localKey()) ?? DEFAULT_CALENDAR_DISPLAY_SETTINGS,
        status: 'ready',
      };
    }
    const generation = this.generation;
    const result = await this.cache.readResult<unknown>(NAMESPACE, SEGMENT);
    if (generation !== this.generation) return { status: 'scope_changed' };
    if (result.status === 'absent') {
      return { settings: DEFAULT_CALENDAR_DISPLAY_SETTINGS, status: 'ready' };
    }
    if (result.status === 'scope_changed') return { status: 'scope_changed' };
    if (result.status === 'read_error') return { status: 'unavailable' };
    const settings = decodeCalendarDisplaySettings(result.value);
    if (settings) return { settings, status: 'ready' };
    const removed = await this.cache.removeResult(NAMESPACE, SEGMENT);
    return removed.status === 'scope_changed'
      ? { status: 'scope_changed' }
      : { status: 'corrupt' };
  }

  async save(settings: CalendarDisplaySettings): Promise<boolean> {
    const canonical = decodeCalendarDisplaySettings(settings);
    if (!canonical) return false;
    if (this.workspaceService.workspace.flavour === 'local') {
      this.syncLocalRealm();
      this.local.set(this.localKey(), canonical);
      return true;
    }
    return this.cache.write(NAMESPACE, SEGMENT, canonical);
  }

  async reset(): Promise<PlannerDisplaySettingsLoadResult> {
    if (this.workspaceService.workspace.flavour === 'local') {
      this.syncLocalRealm();
      this.local.set(this.localKey(), DEFAULT_CALENDAR_DISPLAY_SETTINGS);
      return { settings: DEFAULT_CALENDAR_DISPLAY_SETTINGS, status: 'ready' };
    }
    const removed = await this.cache.removeResult(NAMESPACE, SEGMENT);
    if (removed.status === 'scope_changed') return { status: 'scope_changed' };
    if (removed.status === 'remove_error') return { status: 'unavailable' };
    return (await this.cache.write(
      NAMESPACE,
      SEGMENT,
      DEFAULT_CALENDAR_DISPLAY_SETTINGS
    ))
      ? { settings: DEFAULT_CALENDAR_DISPLAY_SETTINGS, status: 'ready' }
      : { status: 'unavailable' };
  }

  async loadViewSelection(): Promise<PlannerViewSelectionLoadResult> {
    if (this.workspaceService.workspace.flavour === 'local') {
      this.syncLocalRealm();
      return {
        selection: this.localSelections.get(this.localKey()) ?? {
          version: 1,
          views: {},
        },
        status: 'ready',
      };
    }
    const generation = this.generation;
    const result = await this.cache.readResult<unknown>(
      NAMESPACE,
      SELECTION_SEGMENT
    );
    if (generation !== this.generation) return { status: 'scope_changed' };
    if (result.status === 'absent')
      return { selection: { version: 1, views: {} }, status: 'ready' };
    if (result.status === 'scope_changed') return { status: 'scope_changed' };
    if (result.status === 'read_error') return { status: 'unavailable' };
    const selection = decodePlannerViewSelectionV1(result.value);
    if (selection) return { selection, status: 'ready' };
    const removed = await this.cache.removeResult(NAMESPACE, SELECTION_SEGMENT);
    return removed.status === 'scope_changed'
      ? { status: 'scope_changed' }
      : { status: 'corrupt' };
  }

  async savePreferredView(
    ref: PlannerSourceRef,
    kind: 'gantt' | 'kanban',
    viewId: string | null
  ): Promise<boolean> {
    return this.enqueueSelectionUpdate(async () => {
      if (this.workspaceService.workspace.flavour === 'local') {
        this.syncLocalRealm();
      }
      const generation = this.generation;
      const loaded = await this.loadViewSelection();
      if (loaded.status !== 'ready' || generation !== this.generation) {
        return false;
      }
      const state = loaded.selection;
      const key = plannerSourceSelectionKey(ref);
      const next = { ...state.views[key] };
      if (viewId?.trim()) next[kind] = viewId;
      else delete next[kind];
      const views = { ...state.views };
      if (next.gantt || next.kanban) views[key] = next;
      else delete views[key];
      const value: PlannerViewSelectionV1 = { version: 1, views };
      if (this.workspaceService.workspace.flavour === 'local') {
        this.localSelections.set(this.localKey(), value);
        return true;
      }
      return this.cache.write(NAMESPACE, SELECTION_SEGMENT, value);
    });
  }

  private async enqueueSelectionUpdate(
    update: () => Promise<boolean>
  ): Promise<boolean> {
    const result = this.selectionUpdateQueue
      .catch(() => undefined)
      .then(update);
    this.selectionUpdateQueue = result.then(
      () => undefined,
      () => undefined
    );
    try {
      return await result;
    } catch {
      return false;
    }
  }

  private localKey(): string {
    const accountId = this.currentAccountId();
    return `${this.workspaceService.workspace.id}:${accountId ?? LOCAL_ANONYMOUS}`;
  }

  private syncLocalRealm(): void {
    const realm = this.currentAccountId() ?? LOCAL_ANONYMOUS;
    if (this.localRealm !== null && this.localRealm !== realm)
      this.local.clear();
    if (this.localRealm !== null && this.localRealm !== realm)
      this.localSelections.clear();
    if (this.localRealm !== realm) this.generation += 1;
    this.localRealm = realm;
  }

  private currentAccountId(): string | null {
    return (
      (this.workspaceServerService.server ?? this.defaultServerService.server)
        .account$.value?.id ?? null
    );
  }

  override dispose(): void {
    this.local.clear();
    this.localSelections.clear();
    this.localRealm = null;
    super.dispose();
  }
}

function isCanonicalSelectionKey(key: string): boolean {
  try {
    const tuple = JSON.parse(key);
    return (
      Array.isArray(tuple) &&
      tuple.length === 4 &&
      tuple.every(item => typeof item === 'string' && item.trim()) &&
      JSON.stringify(tuple) === key
    );
  } catch {
    return false;
  }
}

function exactObject(
  value: unknown,
  keys: readonly string[]
): value is Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const actual = Object.keys(value).sort();
  const expected = [...keys].sort();
  return (
    actual.length === expected.length &&
    actual.every((key, index) => key === expected[index])
  );
}
